(function (root, factory) {
  const api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.MDEuroshank = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  function positiveInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function buildNestedItems(options) {
    const parentVariantId = positiveInteger(options.parentVariantId, 0);
    const addonVariantId = positiveInteger(options.addonVariantId, 0);
    const quantity = positiveInteger(options.quantity, 1);
    const properties = {};

    Object.keys(options.properties || {}).forEach(function (key) {
      if (options.properties[key] !== '') properties[key] = options.properties[key];
    });

    return [
      { id: parentVariantId, quantity: quantity, properties: properties },
      {
        id: addonVariantId,
        quantity: quantity,
        parent_id: parentVariantId,
        properties: {
          _md_addon_type: 'euroshank',
          _md_parent_variant_id: String(parentVariantId)
        }
      }
    ];
  }

  function calculateDisplayedPrice(basePrice, addonPrice, selected) {
    return Number(basePrice || 0) + (selected ? Number(addonPrice || 0) : 0);
  }

  function buildQuantityUpdates(parentKey, childKey, quantity) {
    const updates = {};
    updates[parentKey] = positiveInteger(quantity, 1);
    updates[childKey] = positiveInteger(quantity, 1);
    return updates;
  }

  const api = {
    buildNestedItems: buildNestedItems,
    calculateDisplayedPrice: calculateDisplayedPrice,
    buildQuantityUpdates: buildQuantityUpdates
  };

  if (!root.document) return api;

  const document = root.document;

  function money(cents) {
    if (root.Shopify && typeof root.Shopify.formatMoney === 'function') {
      return root.Shopify.formatMoney(cents, root.theme && root.theme.moneyFormat);
    }

    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function getFieldset(input) {
    return input && input.closest('[data-md-euroshank]');
  }

  function isSelected(fieldset) {
    return Boolean(fieldset && fieldset.querySelector('[data-md-euroshank-input]:checked'));
  }

  function updateDisplayedPrice(fieldset) {
    const target = document.querySelector(fieldset.dataset.priceTarget)
      || fieldset.closest('.info_content')?.querySelector('.product-single__price');
    const priceData = target && target.querySelector('[data-md-price-data]');
    if (!target || !priceData) return;

    const selected = isSelected(fieldset);
    const addonPrice = Number(fieldset.dataset.addonPrice || 0);
    const basePrice = Number(priceData.dataset.basePrice || 0);
    const baseCompareAtPrice = Number(priceData.dataset.baseCompareAtPrice || 0);
    const currentPrice = calculateDisplayedPrice(basePrice, addonPrice, selected);
    const compareAtPrice = baseCompareAtPrice > basePrice
      ? calculateDisplayedPrice(baseCompareAtPrice, addonPrice, selected)
      : 0;
    const currentNode = target.querySelector('[data-md-current-price]');
    const compareNode = target.querySelector('[data-md-compare-price]');
    const badgeNode = target.querySelector('[data-md-price-badge]');

    if (currentNode) {
      currentNode.textContent = money(currentPrice);
      currentNode.setAttribute('content', (currentPrice / 100).toFixed(2));
    }
    if (compareNode && compareAtPrice) compareNode.textContent = money(compareAtPrice);
    if (badgeNode && compareAtPrice) {
      badgeNode.textContent = '-' + Math.round(((compareAtPrice - currentPrice) * 10000) / compareAtPrice) / 100 + '%';
    }
  }

  function updateAcceleratedCheckout(fieldset) {
    const info = fieldset.closest('.info_content') || document;
    const selected = isSelected(fieldset);
    const parentForm = document.getElementById(fieldset.dataset.productForm) || fieldset.closest('form');
    const parentId = parentForm && parentForm.querySelector('[name="id"]');
    const quantity = parentForm && parentForm.querySelector('[name="quantity"]');

    info.querySelectorAll('[data-md-euroshank-additional]').forEach(function (input) {
      input.disabled = !selected;
      if (input.dataset.mdEuroshankAdditional === 'parent') {
        input.value = parentId ? parentId.value : '';
      }
      if (input.dataset.mdEuroshankAdditional === 'quantity') {
        input.value = quantity ? positiveInteger(quantity.value, 1) : 1;
      }
    });
  }

  function syncFieldset(fieldset) {
    updateDisplayedPrice(fieldset);
    updateAcceleratedCheckout(fieldset);
  }

  function propertiesFromForm(form) {
    const properties = {};
    new FormData(form).forEach(function (value, key) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    });
    return properties;
  }

  async function refreshCartDisplay() {
    const responses = await Promise.all([
      fetch('/cart?view=json', { credentials: 'same-origin' }),
      fetch('/cart.js', { credentials: 'same-origin' })
    ]);
    const markup = await responses[0].text();
    const cart = await responses[1].json();
    const cartInfo = document.getElementById('cart-info');

    if (cartInfo) cartInfo.innerHTML = markup;
    document.querySelectorAll('.CartCount, #CartCountCavas').forEach(function (node) {
      node.textContent = cart.item_count;
    });
    const headerTotal = document.getElementById('header-cart-total');
    if (headerTotal) headerTotal.textContent = money(cart.total_price);
    decorateMiniCart(cart);
  }

  async function addNestedEuroshank(form, fieldset, button) {
    const parentIdInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const items = buildNestedItems({
      parentVariantId: parentIdInput && parentIdInput.value,
      addonVariantId: fieldset.dataset.addonVariantId,
      quantity: quantityInput && quantityInput.value,
      properties: propertiesFromForm(form)
    });

    button.classList.add('loading');
    button.style.pointerEvents = 'none';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      });
      if (!response.ok) throw new Error('Unable to add Euroshank customization');

      if (root.theme && root.theme.cart_status === 'goto_page_cart') {
        root.location.assign('/cart');
        return;
      }

      await refreshCartDisplay();
      if (root.theme && root.theme.cart_status === 'show_minicart') {
        document.getElementById('desktop_cart')?.classList.add('active');
        document.querySelector('.sidebar-overlay')?.classList.add('act');
      }
    } catch (error) {
      const status = fieldset.querySelector('[data-md-euroshank-status]');
      if (status) status.textContent = 'Euroshank could not be added. Please try again.';
      console.error(error);
    } finally {
      button.classList.remove('loading');
      button.style.pointerEvents = '';
    }
  }

  function selectedEuroshankForButton(button) {
    const form = button.closest('form');
    const input = form && form.querySelector('[data-md-euroshank-input]:checked');
    return input ? { form: form, fieldset: getFieldset(input) } : null;
  }

  function interceptAddToCart(event) {
    const button = event.target.closest('.product-form__cart-submit, #AddToCart');
    if (!button) return;
    const selected = selectedEuroshankForButton(button);
    if (!selected) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addNestedEuroshank(selected.form, selected.fieldset, button);
  }

  function interceptFormSubmit(event) {
    const form = event.target;
    if (!form.matches('[id^="product-form-"]')) return;
    const input = form.querySelector('[data-md-euroshank-input]:checked');
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const button = form.querySelector('#AddToCart, [name="add"]');
    if (button) addNestedEuroshank(form, getFieldset(input), button);
  }

  function childParentKey(item) {
    return item.parent_relationship && item.parent_relationship.parent_key;
  }

  function isEuroshankItem(item) {
    return item.properties && item.properties._md_addon_type === 'euroshank';
  }

  function decorateMiniCart(cart) {
    const nodes = document.querySelectorAll('#cart-info .ajaxcart__product');
    const childrenByParent = {};

    cart.items.forEach(function (item) {
      const parentKey = childParentKey(item);
      if (parentKey && isEuroshankItem(item)) childrenByParent[parentKey] = item;
    });

    nodes.forEach(function (node, index) {
      const item = cart.items[index];
      if (!item) return;
      const quantity = node.querySelector('.cart__mini-qty--input');
      const child = childrenByParent[item.key];

      node.dataset.mdLineKey = item.key;
      if (quantity && child) {
        quantity.dataset.mdLineKey = item.key;
        quantity.dataset.mdChildKey = child.key;
      }
      if (isEuroshankItem(item)) {
        node.classList.add('md-euroshank-cart-child');
        node.querySelector('.cart__item-bottom')?.setAttribute('hidden', 'hidden');
        node.querySelectorAll('a[href]').forEach(function (link) { link.removeAttribute('href'); });
      }
    });
  }

  async function fetchAndDecorateMiniCart() {
    if (!document.getElementById('cart-info')) return;
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      decorateMiniCart(await response.json());
    } catch (error) {
      console.error(error);
    }
  }

  async function updatePairedQuantity(input, quantity) {
    const updates = buildQuantityUpdates(input.dataset.mdLineKey, input.dataset.mdChildKey, quantity);
    await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    });

    if (document.body.classList.contains('template-cart')) {
      root.location.reload();
    } else {
      await refreshCartDisplay();
    }
  }

  function interceptQuantityClick(event) {
    const control = event.target.closest('.cart__mini-qty, .js-qty__adjust');
    if (!control) return;
    const wrapper = control.closest('.cart__mini--qty, .cart__qty');
    const input = wrapper && wrapper.querySelector('.cart__mini-qty--input, .cart__qty-input');
    if (!input || !input.dataset.mdChildKey) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const current = positiveInteger(input.value, 1);
    const next = Math.max(1, current + (control.classList.contains('plus') || control.classList.contains('cart__mini-qty--plus') ? 1 : -1));
    input.value = next;
    updatePairedQuantity(input, next);
  }

  function interceptQuantityChange(event) {
    const input = event.target.closest('.cart__mini-qty--input, .cart__qty-input');
    if (!input || !input.dataset.mdChildKey) return;
    event.stopImmediatePropagation();
    updatePairedQuantity(input, positiveInteger(input.value, 1));
  }

  function init() {
    document.querySelectorAll('[data-md-euroshank]').forEach(syncFieldset);
    fetchAndDecorateMiniCart();

    const cartInfo = document.getElementById('cart-info');
    if (cartInfo) {
      new MutationObserver(fetchAndDecorateMiniCart).observe(cartInfo, { childList: true });
    }
  }

  document.addEventListener('click', interceptAddToCart, true);
  document.addEventListener('submit', interceptFormSubmit, true);
  document.addEventListener('click', interceptQuantityClick, true);
  document.addEventListener('change', interceptQuantityChange, true);
  document.addEventListener('change', function (event) {
    const fieldset = getFieldset(event.target);
    if (fieldset) syncFieldset(fieldset);
  });
  document.addEventListener('input', function (event) {
    if (event.target.matches('[name="quantity"]')) {
      document.querySelectorAll('[data-md-euroshank]').forEach(updateAcceleratedCheckout);
    }
  });
  document.addEventListener('md:product-price-updated', function () {
    document.querySelectorAll('[data-md-euroshank]').forEach(syncFieldset);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return api;
});
